<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <IntercomTopbar
        :show-search="false"
        app-name="OnePlaceHQ"
        product-name="Billing"
        :business-name="businessName"
        :user="user"
        @notifications="handleNotifications"
        @settings="handleSettings"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="quotations-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">Quotations</h1>
          <p class="page-subtitle">Create estimates and convert to invoices</p>
        </div>
        <IntercomButton variant="primary" @click="editQuotation = null; showCreateModal = true">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>New Quotation</span>
          <kbd class="shortcut-hint">N</kbd>
        </IntercomButton>
      </div>

      <!-- Search Bar -->
      <div class="search-bar">
        <div class="search-input-wrapper">
          <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="search-input"
            :placeholder="$t('common.search') + '...'"
            @input="handleSearchInput"
          />
          <kbd v-if="!searchQuery" class="search-shortcut-hint">/</kbd>
          <button v-if="searchQuery" class="search-clear" @click="clearSearch">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Quotations Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="quotations"
          :loading="isLoading"
          :sort-by="sortBy"
          :sort-order="sortOrder"
          :selected-index="selectedIndex"
          @sort="handleSort"
          hoverable
          clickable
          @row-click="handleView"
        >
          <template #cell-quotation_date="{ value }">
            {{ formatDate(value) }}
          </template>

          <template #cell-valid_until="{ value }">
            {{ formatDate(value) }}
          </template>

          <template #cell-total_amount="{ value }">
            <div class="amount-cell">
              {{ formatCurrency(value) }}
            </div>
          </template>

          <template #cell-status="{ row }">
            <IntercomBadge :variant="getStatusBadgeVariant(getDisplayStatus(row))">
              {{ formatStatus(getDisplayStatus(row)) }}
            </IntercomBadge>
          </template>

          <template #cell-actions="{ row }">
            <div class="action-buttons">
              <IntercomButton size="sm" variant="ghost" @click.stop="handleView(row)">
                View
              </IntercomButton>
              <IntercomButton
                v-if="row.status === 'draft'"
                size="sm"
                variant="ghost"
                @click.stop="handleEdit(row)"
              >
                Edit
              </IntercomButton>
              <IntercomButton
                v-if="canConvert(row)"
                size="sm"
                variant="primary"
                @click.stop="handleConvert(row)"
              >
                Convert
              </IntercomButton>
              <IntercomButton size="sm" variant="ghost" @click.stop="handlePrint(row)">
                Print
              </IntercomButton>
              <IntercomButton
                v-if="row.status === 'draft'"
                size="sm"
                variant="ghost"
                @click.stop="handleDelete(row)"
              >
                Delete
              </IntercomButton>
            </div>
          </template>
        </IntercomTable>
      </IntercomCard>

      <IntercomPagination
        :offset="offset"
        :total="total"
        :limit="limit"
        @update:offset="(v) => { offset = v; fetch() }"
      />

      <!-- Create/Edit Quotation Modal -->
      <QuotationForm
        v-model:show="showCreateModal"
        :quotation="editQuotation"
        @success="handleFormSuccess"
      />

      <!-- Print Quotation Modal -->
      <IntercomModal
        v-model:show="showPrintModal"
        title="Print Quotation"
        width="860px"
      >
        <div v-if="isPrintLoading" class="print-state">Preparing quotation print...</div>
        <div v-else-if="printError" class="print-state print-error">{{ printError }}</div>
        <div v-else-if="printQuotationData" class="quotation-print-preview" :class="{ 'preview-a5': paperSize === 'A5' }">
          <QuotationPrint
            :quotation-data="printQuotationData"
            :business-data="printBusinessData || {}"
            :customer-data="printCustomerData || {}"
            :bank-accounts="printBankAccounts"
            :paper-size="paperSize"
          />
        </div>

        <template #footer>
          <div class="print-modal-actions">
            <IntercomButton variant="secondary" @click="closePrintModal">
              Close
            </IntercomButton>
            <IntercomButton
              variant="primary"
              :disabled="isPrintLoading || !printQuotationData"
              @click="printQuotation"
            >
              Print
            </IntercomButton>
          </div>
        </template>
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useConfirm } from '@shared/composables/useConfirm.js'
import { usePaginatedList } from '@shared/composables/usePaginatedList.js'
import { usePageShortcuts } from '@shared/composables/usePageShortcuts.js'
import { useToast } from '@shared/composables/useToast.js'
import { useBilling } from '../composables/useBilling.js'
import { useQuotationPrint } from '../composables/useQuotationPrint.js'
import {
  IntercomLayout,
  IntercomTopbar,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomPagination,
  IntercomBadge,
  IntercomModal,
} from '@shared/components/intercom'
import QuotationForm from '../components/QuotationForm.vue'
import QuotationPrint from '../components/QuotationPrint.vue'
import { billingNavSections } from '../config/navigation.js'
import { formatDate } from '../utils/billingUtils.js'

const { t } = useI18n()
const router = useRouter()
const { user, logout } = useAuth()
const { confirm } = useConfirm()
const toast = useToast()
const { listQuotations, getQuotation, deleteQuotation, convertQuotation, businessName, fetchBusinessName } = useBilling()

const navSections = billingNavSections

// State
const {
  items: quotations,
  total,
  loading: isLoading,
  offset,
  limit,
  fetch,
  resetAndFetch,
} = usePaginatedList((params) => listQuotations(params))

const showCreateModal = ref(false)
const editQuotation = ref(null)
const sortBy = ref('quotation_date')
const sortOrder = ref('desc')
const searchQuery = ref('')
const searchInputRef = ref(null)
const {
  showPrintModal,
  isPrintLoading,
  printError,
  printQuotationData,
  printBusinessData,
  printCustomerData,
  printBankAccounts,
  paperSize,
  openQuotationPrint,
  closePrintModal,
  printQuotation,
} = useQuotationPrint()

// Keyboard shortcuts for list view
const { selectedIndex } = usePageShortcuts({
  items: quotations,
  onNew: () => { editQuotation.value = null; showCreateModal.value = true },
  onOpen: (item) => handleView(item),
  onEdit: (item) => handleEdit(item),
  onDelete: (item) => handleDelete(item),
  canEdit: (item) => item.status === 'draft',
  canDelete: (item) => item.status === 'draft',
  searchRef: searchInputRef,
})

// Table columns
const columns = [
  { key: 'quotation_number', title: 'Quotation #', width: '130px', sortable: true },
  { key: 'customer_name', title: 'Customer', width: '200px' },
  { key: 'quotation_date', title: 'Date', width: '130px', sortable: true },
  { key: 'valid_until', title: 'Valid Until', width: '130px', sortable: true },
  { key: 'total_amount', title: 'Total Amount', width: '130px', align: 'right', sortable: true },
  { key: 'status', title: 'Status', width: '120px' },
  { key: 'actions', title: 'Actions', width: '280px', align: 'right' },
]

/**
 * Format currency in INR
 */
const formatCurrency = (val) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val || 0)

/**
 * Get display status — show expired when valid_until < today and status is draft
 */
const getDisplayStatus = (row) => {
  if (row.valid_until && row.status === 'draft') {
    const validDate = new Date(row.valid_until)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (validDate < today) {
      return 'expired'
    }
  }
  return row.status
}

/**
 * Get badge variant for quotation status
 */
const getStatusBadgeVariant = (status) => {
  switch (status) {
    case 'accepted':
      return 'success'
    case 'rejected':
      return 'error'
    case 'expired':
      return 'warning'
    case 'converted':
      return 'success'
    case 'draft':
    default:
      return 'default'
  }
}

/**
 * Format status label
 */
const formatStatus = (status) => {
  if (!status) return ''
  return status.charAt(0).toUpperCase() + status.slice(1)
}

/**
 * Check if quotation can be converted to invoice
 */
const canConvert = (row) => {
  const s = row.status
  return s === 'draft' || s === 'accepted'
}

/**
 * Handle view quotation
 */
const handleView = (quotation) => {
  router.push(`/quotations/${quotation.id}`)
}

/**
 * Handle edit draft quotation — fetch full quotation with items then open form
 */
const handleEdit = async (quotation) => {
  try {
    const response = await getQuotation(quotation.id)
    editQuotation.value = response.quotation || response
    showCreateModal.value = true
  } catch (error) {
    console.error('Failed to fetch quotation for editing:', error)
    toast.error('Failed to load quotation')
  }
}

const handlePrint = async (quotation) => {
  await openQuotationPrint(quotation)
}

/**
 * Handle convert quotation to invoice
 */
const handleConvert = async (quotation) => {
  const ok = await confirm(`Convert quotation "${quotation.quotation_number}" to a draft invoice?`, { title: 'Convert to Invoice' })
  if (!ok) return

  try {
    const result = await convertQuotation(quotation.id)
    const invoiceId = result.invoice?.id || result.data?.invoice?.id
    toast.success('Quotation converted to invoice')
    if (invoiceId) {
      router.push(`/invoices/${invoiceId}`)
    } else {
      await fetch()
    }
  } catch (error) {
    console.error('Failed to convert quotation:', error)
    toast.error('Failed to convert quotation: ' + (error.message || 'Unknown error'))
  }
}

/**
 * Handle delete quotation
 */
const handleDelete = async (quotation) => {
  const ok = await confirm(`Delete quotation "${quotation.quotation_number}"? This cannot be undone.`, { title: 'Delete Quotation' })
  if (!ok) return

  try {
    await deleteQuotation(quotation.id)
    toast.success('Quotation deleted')
    await fetch()
  } catch (error) {
    console.error('Failed to delete quotation:', error)
    toast.error('Failed to delete quotation')
  }
}

/**
 * Handle form success
 */
const handleFormSuccess = async () => {
  showCreateModal.value = false
  editQuotation.value = null
  await fetch()
}

const handleSort = ({ sortBy: newSort, sortOrder: newOrder }) => {
  sortBy.value = newSort
  sortOrder.value = newOrder
  resetAndFetch({ search: searchQuery.value, sort_by: newSort, sort_order: newOrder })
}

const handleSearchInput = () => { resetAndFetch({ search: searchQuery.value, sort_by: sortBy.value, sort_order: sortOrder.value }) }
const clearSearch = () => { searchQuery.value = ''; resetAndFetch({ search: '', sort_by: sortBy.value, sort_order: sortOrder.value }) }

const handleNotifications = () => {
  console.log('Notifications')
}

const handleSettings = () => {
  router.push('/settings')
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusinessName()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.quotations-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-8);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  letter-spacing: var(--letter-spacing-tight);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

/* Search Bar */
.search-bar { display: flex; gap: var(--space-3); margin-bottom: var(--space-6); }
.search-input-wrapper { flex: 1; position: relative; display: flex; align-items: center; }
.search-icon { position: absolute; left: 14px; color: var(--color-gray-400); pointer-events: none; }
.search-input { width: 100%; height: 44px; padding: 0 var(--space-10) 0 44px; font-size: var(--font-size-base); color: var(--color-charcoal); background: var(--color-white); border: 1px solid var(--color-gray-200); border-radius: var(--radius-lg); outline: none; transition: all var(--transition-fast); }
.search-input:focus { border-color: var(--color-blue); box-shadow: 0 0 0 3px rgba(0, 87, 255, 0.1); }
.search-clear { position: absolute; right: 12px; display: flex; align-items: center; justify-content: center; width: 24px; height: 24px; background: var(--color-gray-100); border: none; border-radius: 50%; cursor: pointer; color: var(--color-gray-500); transition: all var(--transition-fast); }
.search-clear:hover { background: var(--color-gray-200); color: var(--color-gray-700); }

.amount-cell {
  text-align: right;
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
}

.action-buttons {
  display: flex;
  gap: var(--space-2);
  justify-content: flex-end;
}

.print-state {
  padding: var(--space-8);
  text-align: center;
  color: var(--color-gray-500);
}

.print-error {
  color: #d03050;
}

.print-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  width: 100%;
}

@media (max-width: 768px) {
  .quotations-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .page-title {
    font-size: 26px;
  }
}
</style>
